import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

export type GridFocusContextValue = {
  /** `gridKey` of the grid that currently owns focus, `null` when none does. */
  focusedGrid: string | null;
  focusGrid: (gridKey: string) => void;
  /** Clears focus ONLY if `gridKey` still owns it — a stale blur cannot steal it back. */
  blurGrid: (gridKey: string) => void;
};

export const GridFocusContext = createContext<GridFocusContextValue | null>(null);

/**
 * Provider to allow grids to communicate with each other,
 * so that they don't fight over focus.
 */
export default function GridFocusProvider({ children }: PropsWithChildren) {
  const [focusedGrid, setFocusedGrid] = useState<string | null>(null);

  const focusGrid = useCallback((gridKey: string) => setFocusedGrid(gridKey), []);
  const blurGrid = useCallback(
    (gridKey: string) => setFocusedGrid((current) => (current === gridKey ? null : current)),
    []
  );

  const value = useMemo<GridFocusContextValue>(
    () => ({ focusedGrid, focusGrid, blurGrid }),
    [focusedGrid, focusGrid, blurGrid]
  );

  return <GridFocusContext.Provider value={value}>{children}</GridFocusContext.Provider>;
}

export const useGridFocus = (): GridFocusContextValue => {
  const context = useContext(GridFocusContext);

  if (!context) {
    throw new Error(
      'Missing GridFocusProvider: a grid must be rendered inside <GridFocusProvider> so that two grids on one page do not fight over focus.'
    );
  }

  return context;
};
